<template lang="pug">
app-main-content-layout(title='')
  .c-consents-page
    //- Header Section
    section.c-consents-page__header
      .c-consents-page__header-content
        h1.c-consents-page__heading Consents
        p.c-consents-page__subtitle Manage user consent records and privacy policies

    //- Warning if no organization selected
    section.c-consents-page__warning(v-if="!selectedOrg")
      el-alert(
        title="No Organization Selected"
        type="warning"
        description="Please select an organization from the dropdown to view consents."
        :closable="false"
        show-icon
      )
    
    template(v-else)
      //- Table Section
      section.c-consents-table-section
        CmsListToolbar(
          v-model="searchQuery"
          search-placeholder="Search by user name..."
          @search="handleSearch"
        )

        //- Skeleton Loader
        template(v-if="loading")
          .c-skeleton-loader
            .c-skeleton-row(v-for="n in 10" :key="n")
              .c-skeleton-cell.c-skeleton-cell--user
              .c-skeleton-cell.c-skeleton-cell--version
              .c-skeleton-cell.c-skeleton-cell--basis
              .c-skeleton-cell.c-skeleton-cell--status
              .c-skeleton-cell.c-skeleton-cell--date
              .c-skeleton-cell.c-skeleton-cell--date
        
        //- Table
        el-table(
          v-else
          :data="filteredItems"
          style="width: 100%"
        )
          el-table-column(width="15")
          el-table-column(label="User" min-width="200")
            template(#default="scope")
              .lh-sm
                .font-black {{ scope.row.user.firstname }} {{ scope.row.user.lastname }}
          el-table-column(label="Policy Version" min-width="120")
            template(#default="scope")
              .lh-sm
                .font-medium {{ scope.row.policyVersion }}
          el-table-column(label="Basis" min-width="120")
            template(#default="scope")
              .lh-sm
                .font-medium {{ scope.row.basis }}
          el-table-column(label="Status" min-width="120")
            template(#default="scope")
              span.crm-badge(:class="scope.row.isActive ? 'crm-badge--teal' : 'crm-badge--gray'") {{ scope.row.isActive ? 'Active' : 'Inactive' }}
          el-table-column(label="Created At" min-width="150")
            template(#default="scope")
              .lh-sm
                .font-medium {{ formatDate(scope.row.createdAt) }}
          el-table-column(label="Retention Expiry" min-width="150")
            template(#default="scope")
              .lh-sm
                .font-medium {{ formatDate(scope.row.retentionExpiry) }}
          
          el-table-column(width="15")
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch,
} from 'vue';
import { useStore } from 'vuex';
import globalShared from '@/shared';
import { organizationActiveConsents } from '@/graphs/gql';
import dayjs from 'dayjs';
import CmsListToolbar from '@/components/CmsListToolbar.vue';

interface User {
  id: string;
  firstname: string;
  lastname: string;
  avatar?: string;
}

interface Consent {
  id: string;
  policyVersion: string;
  basis: string;
  retentionExpiry: string;
  isActive: boolean;
  createdAt: string;
  user: User;
}

export default defineComponent({
  name: 'ManageConsents',
  components: { CmsListToolbar },
  setup() {
    const store = useStore();
    const { makeGraphRequest, getAuthToken, ElMessage } = globalShared();
    
    const loading = ref(false);
    const items = ref<Consent[]>([]);
    const searchQuery = ref('');

    const selectedOrg = computed(() => {
      const org = store.getters['organizations/GET_SELECTED_ORGANIZATION'];
      return org;
    });

    const filteredItems = computed(() => {
      if (!searchQuery.value) {
        return items.value;
      }
      
      const query = searchQuery.value.toLowerCase();
      return items.value.filter((item) => {
        const fullName = `${item.user.firstname} ${item.user.lastname}`.toLowerCase();
        return fullName.includes(query);
      });
    });

    const fetchConsents = async () => {
      if (!selectedOrg.value?.id) {
        items.value = [];
        return;
      }

      const orgId = selectedOrg.value.id;
      
      loading.value = true;
      try {
        const response = await makeGraphRequest(
          'query',
          organizationActiveConsents,
          { orgId },
          getAuthToken(),
        );

        if (response?.errors) {
          console.error('[Consents] GraphQL errors:', response.errors);
          ElMessage.error(response.errors[0]?.message || 'Failed to fetch consents');
          return;
        }

        items.value = response?.data?.organizationActiveConsents || [];
      } catch (error) {
        console.error('[Consents] Error fetching consents:', error);
        ElMessage.error('Failed to fetch consents');
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString: string) => {
      if (!dateString) return 'N/A';
      return dayjs(dateString).format('MM/DD/YYYY HH:mm');
    };

    const handleSearch = () => {
      // Search is handled by computed property
    };

    // Watch for organization changes
    watch(
      () => selectedOrg.value?.id,
      () => {
        fetchConsents();
      },
      { immediate: true },
    );

    return {
      loading,
      items,
      searchQuery,
      filteredItems,
      selectedOrg,
      formatDate,
      handleSearch,
    };
  },
});
</script>

<style lang="scss" scoped>
@import './crm-list';

.c-consents-page {
  @include crm-page;
}

@include crm-header('c-consents-page');
@include crm-table('c-consents-table-section');
@include crm-cells;
@include crm-skeleton;

.c-skeleton-cell {
  &--user {
    width: 200px;
  }

  &--version,
  &--basis {
    width: 120px;
  }

  &--date {
    width: 150px;
  }
}

.font-black {
  font-weight: 500;
  color: $foreground;
}

.font-medium {
  color: $gray-600;
}

@media (max-width: 768px) {
  .c-consents-page {
    padding: 1.5rem;
  }
}

@media (max-width: 480px) {
  .c-consents-page {
    padding: 1rem;
  }
}
</style>
